import React, {FunctionComponent, useEffect, useState} from 'react';
import {Field, useFormikContext} from 'formik';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Common
import {GENERAL} from '../../../../constants';
import {models, types} from '../../../../types';

// Components
import {Tile} from '../../../Tile';
import {ModalSchool} from '../ModalSchool';

// Properties
import * as styles from './FormSchool.styles';
import api from "../../../../api";
import {FormCheckbox, FormRadioGroup, FormSelect} from "../FormInputs";
import {
    TBaseDictionaryField, TBaseObject,
    TCheckValues,
    TCountSubjects,
    TSchool,
    TSelectedSubjectInSchool,
    TSelectHeadOfTheSubjectTeam,
    TSubjectsInSelectHeadOfTheSubjectTeam
} from "../../../../types/types";
import {TPublisher, TSubjects} from "../../../../types/models";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {KINDERGARTEN, PERSONAL_SUBJECTS_TITLE_KINDERGARTEN} from "../../../../constants/general";

type TProps = {
    setCheckValues: (prevState: (prevState: TCheckValues[]) => void) => void,
    checkValues: TCheckValues[],
    formErrors: object,
    setFormErrors: object,
}

const FormSchool: FunctionComponent<TProps> = ({setCheckValues, checkValues, formErrors, setFormErrors}) => {
    const [openModal, setModalStatus] = useState(false);
    const [chosenSchoolDetails, setSchoolDetails] = useState<types.TSchool | null>(null);
    const [earlySchoolEducationLevels, setEarlySchoolEducationLevels] = useState<number[]>([]);
    const [subjectsCount, setSubjectsCount] = useState<TCountSubjects[]>([]);
    const {values, errors, setFieldValue} = useFormikContext();
    const [functions, setFunctions] = useState<TBaseDictionaryField[]>([]);
    const [subjectsList, setSubjectsList] = useState<TSubjects[]>([]);
    const [publishersList, setPublishersList] = useState<TPublisher>({});
    const [selectedFunction, setSelectedFunction] = useState<object>({});
    const [subjectSelectedList, setSubjectSelectedList] = useState<TSelectedSubjectInSchool[]>([]);
    const [schoolsArr, setSchoolsArr] = useState<TSchool[]>([]);
    const [schoolChanger, setSchoolChanger] = useState(false);
    const [schoolChangerOpenModal, setSchoolChangerModalStatus] = useState(false);
    const [schoolChangeId, setSchoolChangeId] = useState<number>(0);
    const [showEarlySchoolEducationLevels, setShowEarlySchoolEducationLevels] = useState<number[]>([]);
    const [selectedSubjectTeam, setSelectedSubjectTeam] = useState<TSelectHeadOfTheSubjectTeam[]>([]);
    const [selectedPublisherOption, setSelectedPublisherOption] = useState<TBaseObject>({});
    const [checked, setChecked] = useState<TBaseObject>({});

    const handleOpenModal = () => {
        setModalStatus(true);
        setSchoolChanger(false);
    };
    const handleCloseModal = () => setModalStatus(false);
    const handleSchoolChangerOpenModal = () => setSchoolChangerModalStatus(true);
    const handleSchoolChangerCloseModal = () => setSchoolChangerModalStatus(false);
    const fetchDictionary = () => {
        api.getDictionary()
            .then(({data}) => {
                setFunctions(data.data.functions);
                setSubjectsList(data.data.subjects);
                const earlySchoolEducationLevelsData = data.data.earlySchoolEducationLevels.map((level: number) => {
                    return {id: level, name: level};
                })
                setEarlySchoolEducationLevels(earlySchoolEducationLevelsData);
            });
    };

    const fetchPublisher = (model: models.TPublisher) => {
        api.getPublisher(model)
            .then(({data}) => {
                setPublishersList((prev: any) => ({...prev, [model.subjectId]: data.data.publishers}));
            });
    };

    useEffect(() => {
        fetchDictionary();
    }, []);

    useEffect(() => {
        setFieldValue('teacherCardSchools', checkValues);

        checkValues.forEach((school) => {
            const isNotOtherSubject = school.subjects && (school.subjects.length === 0
                || school.subjects.filter((s) => s.id !== GENERAL.SUBJECT_OTHER).length === 0);
            const isHeadOfTheSubjectTeamChecked = school.function && school.function.id === Number(GENERAL.FUNCTION_HEAD_OF_THE_SUBJECT_TEAM);
            const schoolIds = Object.keys(selectedFunction);

            if(isNotOtherSubject && isHeadOfTheSubjectTeamChecked){
                setSelectedFunction((prevSelectedFunction) => {
                    const newSelectedFunction = prevSelectedFunction;
                    for (let i=0; i<schoolIds.length; i++){
                        if(schoolIds[i] === `${school.id}`){
                            delete prevSelectedFunction[school.id];
                        }
                    }
                    return newSelectedFunction;
                })
            }
        })
    }, [checkValues]);

    useEffect(() => {
        // if the change button was clicked
        if (schoolChanger){
            const changeSchool = schoolsArr?.map((s) => {
                if (chosenSchoolDetails !== null && s.id === schoolChangeId) {
                    handleChangeSchool(s.id);
                    cleanCheckboxes(s.id);
                    return {
                        id: chosenSchoolDetails?.id,
                        name: chosenSchoolDetails?.name,
                        address: chosenSchoolDetails?.address,
                        levelId: chosenSchoolDetails?.levelId,
                    };
                } else {
                    return s;
                }
            });
            setCheckValues((prevSelectedSchools) => {
                const newSelectedSchools = [...prevSelectedSchools];
                return newSelectedSchools.filter(school => school.id !== schoolChangeId);
            });
            setSchoolsArr(changeSchool);
        // if the remove button was clicked
        } else {
            if(schoolsArr.length <= GENERAL.USER_SELECTION_MAX_COUNT) {
                chosenSchoolDetails !== null && setSchoolsArr([...schoolsArr, chosenSchoolDetails]);
            }
        }
        if(chosenSchoolDetails?.id !== undefined){
            setCheckValues((prevSelectedSchools: TCheckValues[]) => {
                return [...prevSelectedSchools, {id: chosenSchoolDetails?.id}];
            })
        }
    }, [chosenSchoolDetails]);

    const handleSubject = (target: EventTarget, schoolIndex: number, subjectId: number, schoolId: number) => {
        setCheckValues((prevSelectedSchools) =>{
            const newSelectedSchools = [...prevSelectedSchools];
            if(target.checked){
                if(!newSelectedSchools[schoolIndex].subjects){
                    newSelectedSchools[schoolIndex] = {
                        ...newSelectedSchools[schoolIndex],
                        subjects: [
                            {
                                id: subjectId
                            }],
                    }
                } else {
                    if(newSelectedSchools[schoolIndex].subjects){
                        newSelectedSchools[schoolIndex].subjects.push({
                            id: subjectId,
                        })
                    }
                }
            } else {
                if(selectedFunction[schoolId] === Number(GENERAL.FUNCTION_HEAD_OF_THE_SUBJECT_TEAM) && resultFilterSubjectToSelect(schoolId).length === 1){
                    setSelectedFunction((prevSelectedFunction) => ({
                        ...prevSelectedFunction,
                        [`${schoolId}`]: null,
                    }))
                }
                if(newSelectedSchools[schoolIndex].subjects){
                    newSelectedSchools[schoolIndex].subjects = newSelectedSchools[schoolIndex].subjects.filter(
                        (subject) => subject.id !== subjectId
                    );

                    const isOtherSubjectSelected = newSelectedSchools[schoolIndex].subjects.map((subject) => {
                        if(subject.id === GENERAL.SUBJECT_OTHER){
                            return true;
                        } else {
                            return false
                        }
                    })
                    // if head of the subject team function is checked and all subjects unchecked => unchecked head of the subject team function
                    if(newSelectedSchools[schoolIndex].subjects.length === 0 ||
                    (newSelectedSchools[schoolIndex].subjects.length === 1 && isOtherSubjectSelected)) {
                        if(newSelectedSchools[schoolIndex].function && newSelectedSchools[schoolIndex].function.id === Number(GENERAL.FUNCTION_HEAD_OF_THE_SUBJECT_TEAM)){
                            newSelectedSchools[schoolIndex] = {
                                ...newSelectedSchools[schoolIndex],
                                function: {},
                            };
                        }
                    }
                }

            }
            return newSelectedSchools;
        })
    }

    const handleChangeSubject = (target: EventTarget, id: number, schoolIndex: number, subjectId: number, schoolLevelId: number) => {
        // id and name of the selected item
        const addSubjectName: TSubjects = subjectsList.find(subject => subject.id === subjectId);

        if (target.checked) {
            // set if checkbox is checked
            setChecked((prevChecked) => ({
                ...prevChecked,
                [`${id}-${subjectId}`]: true,
            }));

            // enabled select publisher
            enabledSelectPublisherSetSubjectCount(id, subjectId);

            // show early school education levels
            if(subjectId === GENERAL.SUBJECT_EARLY_SCHOOL_EDUCATION) {
                setShowEarlySchoolEducationLevels([...showEarlySchoolEducationLevels, id]);
            }

            // if 3 checkboxes are checked, the rest are disabled
            disabledSubjectCheckboxes(subjectId, id, schoolIndex);

            if(subjectId !== GENERAL.SUBJECT_OTHER){
                // select head of the subject team
                const headOfSubjectTeam: TSubjectsInSelectHeadOfTheSubjectTeam = {
                    'id': addSubjectName.id,
                    'name': addSubjectName.name,
                }
                const selected: TSelectHeadOfTheSubjectTeam = {
                    'id': id,
                    'subjects': headOfSubjectTeam,
                }
                setSelectedSubjectTeam([...selectedSubjectTeam, selected]);

                const model: models.TPublisher = {
                    subjectId: subjectId,
                    schoolLevelId: schoolLevelId,
                }
                fetchPublisher(model);
            }
        } else {
            // set if checkbox is unchecked
            setChecked((prevChecked) => ({
                ...prevChecked,
                [`${id}-${subjectId}`]: false,
            }));

            // select publisherId
            setSelectedPublisherOption((prevSelectedPublisherOption) => {
                delete prevSelectedPublisherOption[`${id}-${subjectId}`];
                const newSelectedPublisherOption = prevSelectedPublisherOption;
                return newSelectedPublisherOption;
            });

           // disabled select publisher
            disabledSelectPublisherSetSubjectCount(id, subjectId);

            // hide early school education levels
            if(subjectId === GENERAL.SUBJECT_EARLY_SCHOOL_EDUCATION){
                setShowEarlySchoolEducationLevels(showEarlySchoolEducationLevels.filter(elem => elem !== id));
            }

            // if 3 checkboxes are checked, the rest are disabled
            enabledSubjectCheckboxes(subjectId, id, schoolIndex);

            //select head of the subject team
            setSelectedSubjectTeam((prevState) => {
                const objectToRemove = prevState.filter(el => el.id === id && el.subjects.id === subjectId);
                return prevState.filter(obj => obj !== objectToRemove[0]);
            })
        }
    }

    const handlerSelectChange = (event: React.ChangeEvent<HTMLInputElement>, schoolId: number, subjectId: number) => {
        handleChangePublisher(event, schoolId, subjectId);
        handleSetOption(event, schoolId, subjectId);
    }

    // disabled select publisher list
    const enabledSelectPublisherSetSubjectCount = (schoolId: number, subjectId: number) => {
        const countSubjects: TCountSubjects = {
            'id': schoolId,
            'subjects': subjectId,
        }
        setSubjectsCount([...subjectsCount, countSubjects]);
    }

    const disabledSelectPublisherSetSubjectCount = (schoolId: number, subjectId: number) => {
        setSubjectsCount((prevState) => {
            const objectToRemove = prevState.filter(el => el.id === schoolId && el.subjects === subjectId);
            return prevState.filter(obj => obj !== objectToRemove[0]);
        })
    }

    const disabledSelectPublisher = (schoolId: number, subjectId: number) => {
        const result = subjectsCount.filter((elem) => {
            return elem.id === schoolId;
        });
        return result.map(obj => obj.subjects).includes(subjectId);
    }

    // filtering subjects to select head of the subject team
    const resultFilterSubjectToSelect = (schoolId: number) => {
        const result = selectedSubjectTeam.filter((elem) => {
            return elem.id === schoolId;
        });
        return result.map(obj => obj.subjects);
    }

    const filterSelectedSubjectTeam = (schoolId: number) => {
        const filterBySchool = selectedSubjectTeam.filter(obj => obj.id === schoolId);
        if(filterBySchool.length !== 0){
            return true;
        }
        return false;
    }

    const handleClickFunction = (event: React.ChangeEvent<HTMLInputElement>, schoolId: number, schoolIndex: number, value: number) => {
        setSelectedFunction((prevSelectedFunction) => ({
            ...prevSelectedFunction,
            [`${schoolId}`]: event.target.checked ? Number(value) : null,
        }));

        if(event.target.checked){
            setCheckValues((prevSelectedSchool) => {
                return prevSelectedSchool.map((school) => {
                    if(school.id === schoolId){
                        return {
                            ...school,
                            function: {
                                id: Number(value),
                            }
                        }
                    }
                    return school;
                });
            });
        } else {
            setCheckValues((prevSelectedSchool) => {
                return prevSelectedSchool.map((school) => {
                    if(school.id === schoolId){
                        return {
                            ...school,
                            function: {},
                        };
                    }
                    return school;
                })
            })
        }
    }

    const handleChangeHeadOfTheSubjectTeam = (event: React.ChangeEvent<HTMLInputElement>, subjectId: number, schoolId: number) => {
        setCheckValues((prevSelectedSchools) => {
            return prevSelectedSchools.map((school) => {
                if(school.id === schoolId){
                    school.function.subjectId = subjectId;
                }
                return school;
            });
        })
    }

    const filterSubjectListBySchoolLevels = (schoolLevel: number): TSubjects[] | null => {
        return subjectsList.filter(obj => obj.possibleLevels.includes(schoolLevel));
    }

    // if 3 checkboxes are checked, the rest are disabled
    const disabledSubjectCheckboxes = (subjectId: number, id: number, schoolIndex: number) => {
        let selectedSubject: number[] = [];

        subjectSelectedList.forEach(el => {
            if (el.id === id) {
                return selectedSubject = el.subjectIds
            }
        })
        const selectedSubjectInSchool: TSelectedSubjectInSchool = {
            'id': id, // schoolId
            'subjectIds': [
                ...selectedSubject, subjectId
            ]
        }

        const newSubjectList: TSelectedSubjectInSchool[] = subjectSelectedList.filter(el => el.id !== id);
        newSubjectList.push(selectedSubjectInSchool);

        setSubjectSelectedList(newSubjectList);
    }

    const handleChangePublisher = (event: React.ChangeEvent<HTMLInputElement>, schoolID: number, subjectId: number) => {
        setCheckValues((prevSelectedSchools) => {
            return prevSelectedSchools.map((school) => {
                if(school.id === schoolID){
                    return {
                        ...school,
                        subjects: school.subjects.map((subject) => {
                            if(subject.id === subjectId){
                                return {
                                    ...subject,
                                    publisherId: event.target.value,
                                }
                            }
                            return subject;
                        })
                    }
                }
                return school;
            })
        })
    };

    const handleChangeEarlySchoolEducationLevel = (earlySchoolEducationLevel: string, subjectId: number, schoolId: number) => {
        setCheckValues((prevSelectedSchools) => {
            return prevSelectedSchools.map((school) => {
                if(school.id === schoolId){
                    return {
                        ...school,
                        subjects: school.subjects.map((subject) => {
                            if(subject.id === subjectId){
                                return {
                                    ...subject,
                                    earlySchoolEducationLevel: Number(earlySchoolEducationLevel),
                                }
                            }
                            return subject;
                        })
                    }
                }
                return school;
            })
        })
    }

    const enabledSubjectCheckboxes = (subjectId: number, id: number, schoolIndex: number) => {
        let selectedSubject: number[] = [];

        subjectSelectedList.forEach(el => {
            if (el.id === id) {
                return selectedSubject = el.subjectIds.filter(el => el !== subjectId);
            }
        })
        const replacingObj: TSelectedSubjectInSchool = {
            'id': id, // schoolId
            'subjectIds': [
                ...selectedSubject
            ]
        }
        if(selectedSubject.length < 1){
            setSubjectSelectedList(subjectSelectedList.filter(el => el.id !== replacingObj.id));
        } else {
            const i = subjectSelectedList.findIndex(el => el.id === replacingObj.id);
            subjectSelectedList[i] = replacingObj;
            const newSubjectList: TSelectedSubjectInSchool[] = subjectSelectedList;
            setSubjectSelectedList(newSubjectList);
        }
    }
    const isCheckboxDisabled = (id: number, schoolId: number): boolean => {
        const currentSchoolSubjects = subjectSelectedList.filter(el => el.id === schoolId);
        return currentSchoolSubjects.length > 0
            ? !currentSchoolSubjects[0].subjectIds.includes(id) && currentSchoolSubjects[0].subjectIds.length >= 3
            : false;
    }

    const handleSetOption = (event: React.ChangeEvent<HTMLInputElement>, schoolId: number, subjectId: number) => {
        setSelectedPublisherOption((prevSelectedPublisherOption) => ({
            ...prevSelectedPublisherOption,
            [`${schoolId}-${subjectId}`] : event.target.value,
        }));
    }

    const removeSchoolFromPublisherOption = (schoolId: number) => {
        const keys = Object.keys(selectedPublisherOption);
        const findSchoolIdInKey = keys.map((k) => {
            if(k.includes(`${schoolId}-`)){
                return k;
            }
        });
        const keysToRemove = findSchoolIdInKey.filter((k) => k !== undefined);

        setSelectedPublisherOption((prevSelectedPublisherOption) => {
            keysToRemove.forEach(e => delete prevSelectedPublisherOption[e]);
            const newSelectedPublisherOption = prevSelectedPublisherOption;
            return newSelectedPublisherOption;
        })
    }

    const cleanCheckboxes = (schoolId: number) => {
        const keys = Object.keys(checked);
        const findSchoolIdInKey = keys.map((k) => {
            if(k.includes(`${schoolId}-`)){
                return k;
            }
        });
        const keysToRemove = findSchoolIdInKey.filter((k) => k !== undefined);

        setChecked((prevChecked) => {
            keysToRemove.forEach(e => delete prevChecked[e]);
            const newPrevChecked = prevChecked;
            return newPrevChecked;
        });
    }

    const handleRemoveSchool = (schoolId: number) => {
        setSchoolsArr(schoolsArr.filter(s => s.id !== schoolId));
        setShowEarlySchoolEducationLevels(showEarlySchoolEducationLevels.filter(elem => elem !== schoolId));
        setSelectedSubjectTeam(selectedSubjectTeam.filter(elem => elem.id !== schoolId));
        setSubjectsCount(subjectsCount.filter(elem => elem.id !== schoolId));
        setSubjectSelectedList(subjectSelectedList.filter(elem => elem.id !== schoolId));
        setCheckValues((prevSelectedSchools) => {
            const newSelectedSchools = [...prevSelectedSchools];
            return newSelectedSchools.filter(school => school.id !== schoolId);
        });
        removeSchoolFromPublisherOption(schoolId);
        cleanCheckboxes(schoolId);
    }

    const handleChangeSchool = (schoolId: number) => {
        setSelectedSubjectTeam(selectedSubjectTeam.filter(elem => elem.id !== schoolId));
        setShowEarlySchoolEducationLevels(showEarlySchoolEducationLevels.filter(elem => elem !== schoolId));
        setSubjectsCount(subjectsCount.filter(elem => elem.id !== schoolId));
        setSubjectSelectedList(subjectSelectedList.filter(elem => elem.id !== schoolId));
        removeSchoolFromPublisherOption(schoolId);
    }

    const handleButtonChangeSchool = (schoolId: number) => {
        setSchoolChanger(true);
        handleSchoolChangerOpenModal();
        setSchoolChangeId(schoolId);
    }

    return (
        <Tile title={GENERAL.SCHOOL_TITLE}
              subtitle={
                  schoolsArr.length !== 0 ? (
                      <Typography variant="caption">
                          {GENERAL.SCHOOL_DESCRIPTION[1]}
                          <Link
                              underline="none"
                              href={GENERAL.PAPER_FORM_TEACHER_SCHOOL_CARD}
                              sx={styles.link}
                          >
                              <Box
                                  component="span"
                                  sx={styles.italic}
                              >
                                  {GENERAL.SCHOOL_DESCRIPTION[2]}
                              </Box>
                              {GENERAL.SCHOOL_DESCRIPTION[3]}
                          </Link>.
                          {GENERAL.SCHOOL_DESCRIPTION[10]}
                          {GENERAL.SCHOOL_DESCRIPTION[4]}
                          <Box
                              component="span"
                              sx={styles.italic}
                          >
                              {GENERAL.SCHOOL_DESCRIPTION[5]}
                          </Box>
                          {GENERAL.SCHOOL_DESCRIPTION[6]}
                          {GENERAL.SCHOOL_DESCRIPTION[7]}
                          {GENERAL.SCHOOL_DESCRIPTION[8]}
                          <Link
                              underline="none"
                              href={`mailto:${GENERAL.SCHOOL_EMAIL}`}
                          >
                              {GENERAL.SCHOOL_EMAIL}
                          </Link>
                          {GENERAL.SCHOOL_DESCRIPTION[9]}
                      </Typography>
                  ) : null
              }
        >
            <Box>
                {
                    schoolsArr.length > 0 ? schoolsArr.map((school, index) => (
                        <Box key={index}>
                            <Box sx={styles.school}>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={styles.name}
                                    >
                                        {school.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        {school.address}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={styles.buttonContainer}
                                >
                                    <Box>
                                        <Link
                                            component="button"
                                            underline="none"
                                            onClick={() => {
                                                handleRemoveSchool(school.id);
                                            }
                                            }
                                            sx={styles.deleteButton}
                                        >
                                            <DeleteOutlineIcon/>
                                        </Link>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            handleButtonChangeSchool(school.id);
                                        }
                                        }
                                        sx={styles.changeButton}
                                    >
                                        {GENERAL.SCHOOL_CHANGE_BUTTON}
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={styles.fieldsSubjectsWrapper}>
                                <Box sx={styles.subjectTitle}>
                                    {
                                        school.levelId === KINDERGARTEN ? (
                                            <Typography variant='caption'>
                                                {GENERAL.PERSONAL_SUBJECTS_TITLE_KINDERGARTEN}
                                            </Typography>
                                        ) : (
                                            <Typography variant='caption'>
                                                {GENERAL.PERSONAL_SUBJECTS_TITLE}
                                            </Typography>
                                        )
                                    }
                                </Box>
                                <Box sx={styles.fieldsFullRow}>
                                    <Box sx={styles.fieldsHalfRow}>
                                        <Typography variant="caption">
                                            {GENERAL.PERSONAL_SUBJECTS}
                                        </Typography>
                                    </Box>
                                    <Box sx={styles.fieldsHalfRow}>
                                        <Typography variant="caption">
                                            {GENERAL.PERSONAL_PUBLISHERS}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={styles.fieldsSubjects}>
                                    {
                                        filterSubjectListBySchoolLevels(school.levelId)?.map((subject, i) => (
                                            <Box key={subject.id}>
                                                <Box
                                                    sx={(theme) => {
                                                        return {...styles.fieldsFullRow, ...styles.fieldSubjects(theme)}
                                                    }}
                                                >
                                                    <Box sx={styles.fieldsHalfRow}>
                                                        <Field
                                                            name={`teacherCardSchools[${index}].subjects[${i}].id[${subject.id}]`}
                                                            disabled={isCheckboxDisabled(subject.id, school.id)}
                                                            label={subject.name}
                                                            value={subject.id}
                                                            handleChange={e => {
                                                                handleChangeSubject(e.target, school.id, index, subject.id, school.levelId);
                                                                handleSubject(e.target, index, subject.id, school.id);
                                                            }}
                                                            isCheckboxGroup
                                                            checked={checked[`${school.id}-${subject.id}`] || !checked[`${school.id}-${subject.id}`] == undefined}
                                                            component={FormCheckbox}
                                                        />
                                                    </Box>
                                                    <Box sx={(theme) => {
                                                        return {...styles.fieldsHalfRow, ...styles.fieldPublishers(theme)}
                                                    }}>
                                                        {
                                                            subject.id !== GENERAL.SUBJECT_OTHER && (
                                                                <FormControl
                                                                    disabled={!disabledSelectPublisher(school.id, subject.id)}
                                                                    sx={styles.selectField}
                                                                >
                                                                    <Select
                                                                        name={`publisherId[${school.id}]`}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        displayEmpty
                                                                        defaultValue={''}
                                                                        value={!(`${school.id}-${subject.id}` in selectedPublisherOption) ? '' : selectedPublisherOption[`${school.id}-${subject.id}`]}
                                                                        onChange={(e) => {
                                                                            handlerSelectChange(e, school.id, subject.id);
                                                                        }
                                                                    }
                                                                    >
                                                                        <MenuItem
                                                                            key={''}
                                                                            value={''}
                                                                        >
                                                                            {GENERAL.SELECT_DEFAULT}
                                                                        </MenuItem>
                                                                        {
                                                                            publishersList[subject.id] && (
                                                                                publishersList[subject.id] ? publishersList[subject.id].map((option: TBaseDictionaryField) => (
                                                                                    <MenuItem
                                                                                        key={option.id}
                                                                                        value={option.id}
                                                                                    >
                                                                                        {option.name}
                                                                                    </MenuItem>
                                                                                )) : null
                                                                            )
                                                                        }
                                                                    </Select>
                                                                </FormControl>
                                                            )
                                                        }
                                                    </Box>
                                                </Box>
                                                {subject.id === GENERAL.SUBJECT_EARLY_SCHOOL_EDUCATION && (
                                                    showEarlySchoolEducationLevels.includes(school.id) && (
                                                        <>
                                                            <Box sx={styles.earlySchoolEducationLevel}>
                                                                <Field
                                                                    name={`teacherCardSchools[${index}].subjects[${i}].earlySchoolEducationLevel`}
                                                                    label={GENERAL.PERSONAL_LEVEL}
                                                                    options={earlySchoolEducationLevels}
                                                                    required
                                                                    row
                                                                    component={FormRadioGroup}
                                                                    onClick={e => {
                                                                        handleChangeEarlySchoolEducationLevel(e.target.value, subject.id, school.id)
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Box sx={styles.errorEW}>
                                                                {
                                                                    formErrors && (
                                                                        <Typography variant="caption">
                                                                            {formErrors[`earlySchoolEducationLevel-${school.id}`]}
                                                                        </Typography>
                                                                    )
                                                                }
                                                            </Box>
                                                        </>
                                                    )
                                                )}
                                            </Box>
                                        ))
                                    }
                                    <Box sx={styles.fieldsFullRow}>
                                        <Box sx={styles.fieldsHalfRow}>
                                            {
                                                formErrors && (
                                                    <Typography variant="caption" sx={styles.error}>
                                                        {formErrors[`subjects-${school.id}`]}
                                                    </Typography>
                                                )
                                            }
                                        </Box>
                                        <Box sx={styles.fieldsHalfRow}>
                                            {
                                                formErrors && (
                                                    <Typography variant="caption" sx={styles.error}>
                                                        {formErrors[`publisherId-${school.id}`]}
                                                    </Typography>
                                                )
                                            }
                                        </Box>
                                    </Box>
                                    <Box sx={styles.functionTitle}>
                                        <Typography variant='caption'>
                                            {GENERAL.PERSONAL_FUNCTION}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {
                                            functions.map((fun) => (
                                                <Field
                                                    name={`teacherCardSchools[${index}].function.id`}
                                                    label={fun.name}
                                                    handleChange={e => {
                                                        handleClickFunction(e, school.id, index, fun.id);
                                                    }}
                                                    disabled={fun.id === Number('4') && !filterSelectedSubjectTeam(school.id)}
                                                    value={fun.id}
                                                    isCheckboxGroup
                                                    checked={selectedFunction[`${school.id}`] === fun.id}
                                                    component={FormCheckbox}
                                                />)
                                            )
                                        }
                                        {
                                            selectedFunction[`${school.id}`] === Number(GENERAL.FUNCTION_HEAD_OF_THE_SUBJECT_TEAM) &&
                                            resultFilterSubjectToSelect(school.id).length !== 0 ?
                                                functions.map((f) => (
                                                    f.id === Number(GENERAL.FUNCTION_HEAD_OF_THE_SUBJECT_TEAM) && (
                                                        <Field
                                                            as="select"
                                                            name={`teacherCardSchools[${index}].function.subjectId`}
                                                            options={resultFilterSubjectToSelect(school.id)}
                                                            fullWidth
                                                            isSelectGroup
                                                            handleChange={e => {
                                                                handleChangeHeadOfTheSubjectTeam(e, e.target.value, school.id);
                                                            }}
                                                            component={FormSelect}
                                                        />
                                                    )
                                                )) : null
                                        }
                                        <Box sx={styles.fieldsFullRow}>
                                            <Box sx={styles.fieldsHalfRow}>
                                            </Box>
                                            <Box sx={styles.fieldsHalfRow}>
                                                {
                                                    formErrors && (
                                                        <Typography variant="caption" sx={styles.error}>
                                                            {formErrors[`headOfTheSubjectTeam-${school.id}`]}
                                                        </Typography>
                                                    )
                                                }
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )): null
                }
                <Box>
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleOpenModal();
                        }}
                        disabled={schoolsArr.length >= 3}
                    >
                        {schoolsArr.length === 0 ? GENERAL.SCHOOL_SELECT_BUTTON : GENERAL.CHOOSE_ANOTHER_SCHOOL}
                    </Button>
                </Box>
            </Box>
            <Box sx={styles.error}>
                {
                    formErrors && (
                        <Typography variant="caption" sx={styles.error}>
                            {formErrors['school']}
                        </Typography>
                    )
                }
            </Box>
            {
                schoolsArr.length === 0 ? (
                    <Box sx={styles.text}>
                        <Typography variant="caption">
                            {GENERAL.SCHOOL_DESCRIPTION[1]}
                            <Link
                                underline="none"
                                href={GENERAL.PAPER_FORM_TEACHER_SCHOOL_CARD}
                                sx={styles.link}
                            >
                                <Box
                                    component="span"
                                    sx={styles.italic}
                                >
                                    {GENERAL.SCHOOL_DESCRIPTION[2]}
                                </Box>
                                {GENERAL.SCHOOL_DESCRIPTION[3]}
                            </Link>.
                            <br/>
                            {GENERAL.SCHOOL_DESCRIPTION[4]}
                            <Box
                                component="span"
                                sx={styles.italic}
                            >
                                {GENERAL.SCHOOL_DESCRIPTION[5]}
                            </Box>
                            {GENERAL.SCHOOL_DESCRIPTION[6]}
                            <br/>
                            {GENERAL.SCHOOL_DESCRIPTION[7]}
                            <br/>
                            {GENERAL.SCHOOL_DESCRIPTION[8]}
                            <Link
                                underline="none"
                                href={`mailto:${GENERAL.SCHOOL_EMAIL}`}
                            >
                                {GENERAL.SCHOOL_EMAIL}
                            </Link>
                            {GENERAL.SCHOOL_DESCRIPTION[9]}
                        </Typography>
                    </Box>
                ) : null
            }
            {
                openModal && (
                    <ModalSchool
                        open={openModal}
                        onClose={handleCloseModal}
                        setSchoolDetails={setSchoolDetails}
                        schoolsArr={schoolsArr}
                    />
                )
            }
            {
                schoolChangerOpenModal && (
                    <ModalSchool
                        open={schoolChangerOpenModal}
                        onClose={handleSchoolChangerCloseModal}
                        setSchoolDetails={setSchoolDetails}
                        schoolsArr={schoolsArr}
                    />
                )
            }
        </Tile>
    );
};

export default FormSchool;
